// src/pages/VotingPage.js
// Updated: 11/10/2025 07:45 PM EST
// --- COPY-PASTE THIS ENTIRE FILE ---

import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, onSnapshot, orderBy, collectionGroup, getDocs } from 'firebase/firestore';
import { Plus } from 'lucide-react';
import CreateMotionModal from '../components/CreateMotionModal';
import MotionCard from '../components/MotionCard';

const LoadingSpinner = () => (
    <div className="flex justify-center items-center h-32">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-dashed border-blue-500"></div>
    </div>
);

export default function VotingPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [motions, setMotions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const { userData, currentUser } = useAuth();
  const isAdmin = userData?.role === 'admin';
  const currentUserId = currentUser?.uid;

  const [votedMotionIds, setVotedMotionIds] = useState(new Set());
  const [isLoadingVotes, setIsLoadingVotes] = useState(true);

  // Effect 1: Fetch all votes cast by the current user in this org (No change needed here)
  useEffect(() => {
    if (!currentUserId || !userData?.orgId) {
      setIsLoadingVotes(false);
      return;
    }

    setIsLoadingVotes(true);
    const votesQuery = query(
      collectionGroup(db, 'votes'),
      where('userId', '==', currentUserId),
      where('orgId', '==', userData.orgId)
    );

    getDocs(votesQuery)
      .then((snapshot) => {
        const ids = new Set();
        snapshot.forEach((doc) => {
          ids.add(doc.ref.parent.parent.id);
        });
        setVotedMotionIds(ids);
        console.log(`VotingPage: Found ${ids.size} motions voted on by user ${currentUserId}`);
      })
      .catch((err) => {
        console.error("Error fetching user's votes:", err);
      })
      .finally(() => {
        setIsLoadingVotes(false);
      });

  }, [currentUserId, userData?.orgId]);


  // Effect 2: Fetch and filter motions
  useEffect(() => {
    // We only need the user's orgId, not the whole userData object.
    const orgId = userData?.orgId;

    if (!orgId || !currentUserId) {
        if (userData !== undefined && currentUser !== undefined) {
             setIsLoading(false);
             setError("User organization information or User ID is missing.");
             console.warn("VotingPage: User data/orgId or currentUser/uid is null/undefined.");
        }
        return;
    };

    if (!isLoadingVotes) {
      setIsLoading(true);
    }
    setError('');
    console.log("VotingPage: Setting up motion listener for org:", orgId);

    const q = query(
      collection(db, 'motions'),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const motionsData = [];
        querySnapshot.forEach((doc) => {
          motionsData.push({ id: doc.id, ...doc.data() });
        });

        // Wait until vote loading is finished before filtering
        if (!isLoadingVotes) {
            const filteredMotions = motionsData.filter(motion => {
              // Admins see all motions
              if (isAdmin) {
                return true;
              }

              const isRestricted = motion.allowedVoters && Array.isArray(motion.allowedVoters);
              const isUserAllowed = isRestricted ? motion.allowedVoters.includes(currentUserId) : false; // Check only if restricted
              const userVoted = votedMotionIds.has(motion.id); // Check if user voted
              const isPublic = motion.resultsPublic === true; // Check public flag

              // --- Visibility rules for NON-ADMINS ---
              // 1. Show ACTIVE motions if unrestricted OR user is allowed
              if (motion.status === 'open') {
                return !isRestricted || isUserAllowed;
              }

              // 2. Show CLOSED motions if:
              if (motion.status === 'closed') {
                // a) It was restricted AND user was allowed (implies they could have voted)
                if (isRestricted && isUserAllowed) {
                  return true;
                }
                // b) It was unrestricted AND marked public
                if (!isRestricted && isPublic) {
                  return true;
                }
                // c) The user voted on it (regardless of public status or initial restriction)
                if (userVoted) {
                  return true;
                }
              }

              // Otherwise, hide the motion
              return false;
            });
            // --- END UPDATED FILTERING LOGIC ---

            setMotions(filteredMotions);
            setIsLoading(false); // Set final loading state
            console.log(`VotingPage: Loaded ${motionsData.length} motions, showing ${filteredMotions.length} after filtering.`);
        } // else: still waiting for votes to load, do nothing yet
      },
      (error) => {
        console.error('Error fetching motions:', error);
        setError(`Failed to load motions. Error: ${error.message}`);
        setIsLoading(false);
      }
    );

    return () => {
        console.log("VotingPage: Cleaning up motion listener.");
        unsubscribe();
    }
  // --- FIX: Include userData directly in the dependency array ---
  // The hook should only re-run if these values change.
  }, [userData, currentUserId, isAdmin, isLoadingVotes, votedMotionIds, currentUser]);
  // --- END OF FIX ---

  // Use combined loading state for rendering spinners/messages
  const showLoading = isLoading || isLoadingVotes;

  const activeMotions = motions.filter((m) => m.status === 'open');
  const closedMotions = motions.filter((m) => m.status === 'closed');

  return (
    <>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Voting & Motions</h1>
        {isAdmin && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
          >
            <Plus className="mr-2 h-5 w-5" />
            Create Motion
          </button>
        )}
      </div>

      {error && (
          <div className="mt-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
              {error}
          </div>
      )}

      {showLoading && <LoadingSpinner />}

      {/* Active Motions */}
      {!showLoading && (
          <div className="mt-8">
            <h2 className="text-2xl font-semibold text-gray-800">
              Active Motions
            </h2>
            {activeMotions.length === 0 && !error &&
              <p className="mt-2 text-gray-500">No active motions visible to you.</p>
            }
            <div className="mt-4 grid grid-cols-1 gap-6">
              {activeMotions.map((motion) => (
                <MotionCard key={motion.id} motion={motion} />
              ))}
            </div>
          </div>
      )}


      {/* Closed Motions */}
       {!showLoading && (
          <div className="mt-12">
            <h2 className="text-2xl font-semibold text-gray-800">
              Closed Motions
            </h2>
            {closedMotions.length === 0 && !error &&
              <p className="mt-2 text-gray-500">No closed motions visible to you.</p>
            }
            <div className="mt-4 grid grid-cols-1 gap-6">
              {closedMotions.map((motion) => (
                <MotionCard key={motion.id} motion={motion} />
              ))}
            </div>
          </div>
       )}


      {/* The "Create Motion" Modal */}
      <CreateMotionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}